import { useEffect, useMemo, useState } from "react";
import { TextInput } from "@ferrivia/ui";
import type { SupplementProduct } from "@ferrivia/api-contract";
import { useTracker } from "@ferrivia/auth-web";
import { useMarket } from "@ferrivia/i18n";
import { useQuestionnaireCopy, type SupplementPickerCopy } from "@ferrivia/questionnaire-ui";
import { useSupplementCatalog } from "../../hooks/useSupplementCatalog";
import { focusWithoutPan } from "../../onboarding/focusWithoutPan";
import f from "./fieldBox.module.css";
import s from "./SupplementPicker.module.css";

// The data layer ships exactly one instrument (see QuestionnaireOverlay's own
// comment on INSTRUMENT) -- not exported from api-contract as a value, only
// as the enum type, so it's repeated here rather than threaded down through
// every layer between QuestionnaireOverlay and this leaf control.
const INSTRUMENT = "health_questionnaire" as const;

export interface SupplementPickerProps {
  labelledBy: string;
  name: string;
  productId: string;
  onNameChange(value: string): void;
  onProductIdChange(value: string): void;
  /** Lets the sibling `daily_iron_dose` field (rendered by `RepeatingGroupItem`,
      not by this component) show the product's label figure as helper text.
      Display only -- this never touches the dose answer itself, see the
      compliance note on `doseHint` in `handleSelect`/the sync effect below. */
  onDoseHintChange?(hint: string | undefined): void;
  /** The field's `catalogCategory`: limits the search list to that category.
      Unset lists the whole catalog. */
  category?: "iron" | "other";
  errorText?: string;
}

// ® and the SQL-escaped apostrophe ("Bertil''s Hemoglobiini", from
// 037_supplement_catalog.sql) would otherwise break a plain substring match
// ("bertils", "sideral").
function normalize(value: string | null): string {
  return (value ?? "").toLowerCase().replace(/®/g, "").replace(/['’]/g, "");
}

function factsFor(product: SupplementProduct, copy: SupplementPickerCopy): string {
  const parts: string[] = [];
  if (product.brand) parts.push(product.brand);
  if (product.elementalIronMg !== null) {
    const unit = copy.unitLabelFor(product.unitLabel);
    parts.push(`${product.elementalIronMg} mg${unit ? ` / ${unit}` : ""}`);
  }
  return parts.join(" · ");
}

/** `iron_supplements.name` / `other_supplement_items.name`'s control
    (`field.control === "supplement_picker"`): a search-and-select box over
    the curated catalog (`useSupplementCatalog`), with "Ei listalla — kirjoita
    itse" as a permanent escape to free text.

    Mode is derived from props, not tracked as its own piece of state, so a
    resumed answer (`productId` already set, or `name` set without one)
    lands in the right mode on mount without an effect: `productId` set means
    a catalog pick (`picked`); `name` set without `productId` means a client
    who already typed free text, or the catalog failing to load, or the
    client having tapped the escape (`escaped`) -- all render the same plain
    text input. Outside Finland the catalog is never loaded, so the field is
    always free text: it lists Finnish products only. Anything else is the searchable list (`search`), the only
    mode the catalog needs to be loaded for. */
export function SupplementPicker({
  labelledBy,
  name,
  productId,
  onNameChange,
  onProductIdChange,
  onDoseHintChange,
  category,
  errorText,
}: SupplementPickerProps) {
  const { state } = useSupplementCatalog({ enabled: useMarket() === "fi" });
  const copy = useQuestionnaireCopy();
  const tracker = useTracker();
  const [query, setQuery] = useState("");
  const [escaped, setEscaped] = useState(false);

  const catalogReady = state.status === "success";
  const mode: "picked" | "freetext" | "search" =
    productId !== "" ? "picked" : !catalogReady || escaped || name !== "" ? "freetext" : "search";

  const selectedProduct = useMemo(
    () => (state.status === "success" ? state.data.products.find((p) => p.id === productId) : undefined),
    [state, productId],
  );

  useEffect(() => {
    if (mode !== "picked" || !selectedProduct || selectedProduct.elementalIronMg === null) {
      onDoseHintChange?.(undefined);
      return;
    }
    onDoseHintChange?.(copy.picker.doseHint(selectedProduct.elementalIronMg, selectedProduct.unitLabel));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedProduct?.id, selectedProduct?.elementalIronMg, selectedProduct?.unitLabel]);

  function handleSelect(product: SupplementProduct) {
    onNameChange(product.name);
    onProductIdChange(product.id);
    setEscaped(false);
    setQuery("");
    tracker.track("questionnaire.supplement_picked", { instrument: INSTRUMENT, from_catalog: true });
  }

  function handleClear() {
    onNameChange("");
    onProductIdChange("");
    setEscaped(false);
    setQuery("");
  }

  function handleEscape() {
    setEscaped(true);
  }

  function handleFreeTextBlur() {
    if (name.trim() === "") return;
    tracker.track("questionnaire.supplement_picked", { instrument: INSTRUMENT, from_catalog: false });
  }

  if (mode === "picked") {
    return (
      <div className={s.root}>
        <div className={s.picked}>
          <div>
            <span className={s.resultName}>
              {name}
              {selectedProduct?.kind === "medicine" ? <span className={s.tag}>{copy.picker.medicineTag}</span> : null}
            </span>
            {selectedProduct && factsFor(selectedProduct, copy.picker) ? (
              <span className={s.resultFacts}>{factsFor(selectedProduct, copy.picker)}</span>
            ) : null}
          </div>
          <button type="button" className={s.clear} onClick={handleClear}>
            {copy.picker.changeSelection}
          </button>
        </div>
        {errorText ? (
          <span role="alert" className={s.error}>
            {errorText}
          </span>
        ) : null}
      </div>
    );
  }

  if (mode === "freetext") {
    return (
      <div className={s.root}>
        <TextInput
          className={f.box}
          aria-labelledby={labelledBy}
          placeholder={copy.picker.freeTextPlaceholder}
          value={name}
          onMouseDown={focusWithoutPan}
          aria-invalid={Boolean(errorText)}
          onChange={(event) => onNameChange(event.target.value)}
          onBlur={handleFreeTextBlur}
        />
        {errorText ? (
          <span role="alert" className={s.error}>
            {errorText}
          </span>
        ) : null}
      </div>
    );
  }

  // mode === "search": only reachable once the catalog has loaded.
  const catalog = state.status === "success" ? state.data.products : [];
  const products = category ? catalog.filter((product) => product.category === category) : catalog;
  const normalizedQuery = normalize(query);
  // Catalog array order, always -- never re-sorted by match quality,
  // relevance or anything else (root CLAUDE.md; the contract's own header
  // comment on supplements.ts). A plain substring filter on what the client
  // types into the picker itself is fine; nothing here reorders.
  const matches =
    normalizedQuery === ""
      ? products
      : products.filter(
          (product) => normalize(product.name).includes(normalizedQuery) || normalize(product.brand).includes(normalizedQuery),
        );

  return (
    <div className={s.root}>
      <TextInput
        className={f.box}
        type="search"
        aria-labelledby={labelledBy}
        placeholder={copy.picker.searchPlaceholder}
        value={query}
        onMouseDown={focusWithoutPan}
        aria-invalid={Boolean(errorText)}
        onChange={(event) => setQuery(event.target.value)}
      />
      <p aria-live="polite" className={s.liveRegion}>
        {copy.picker.resultsCount(matches.length)}
      </p>
      <div className={s.results} aria-label={copy.picker.resultsLabel}>
        {matches.length === 0 ? (
          <p className={s.empty}>{copy.picker.noResults}</p>
        ) : (
          matches.map((product) => (
            <button key={product.id} type="button" className={s.result} onClick={() => handleSelect(product)}>
              <span className={s.resultName}>
                {product.name}
                {product.kind === "medicine" ? <span className={s.tag}>{copy.picker.medicineTag}</span> : null}
              </span>
              {factsFor(product, copy.picker) ? <span className={s.resultFacts}>{factsFor(product, copy.picker)}</span> : null}
            </button>
          ))
        )}
      </div>
      <button type="button" className={s.escape} onClick={handleEscape}>
        {copy.picker.escapeToFreeText}
      </button>
      {errorText ? (
        <span role="alert" className={s.error}>
          {errorText}
        </span>
      ) : null}
    </div>
  );
}
